import { Badge, Box, Button, Center, HStack, Spinner, Stack, Text, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiAward, FiExternalLink, FiFileText } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ReceivedPublicBidDto,
  SourcingEventStatus,
  SourcingEventV2Dto,
  useConfirmAwardMutation,
  useGetEvaluationQuery,
  useListPublicBidsForEventQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { contractUrl } from "../contractUrl";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { AwardLetterWorkbench } from "./AwardLetterWorkbench";
import { CreateContractFromBidModal } from "./CreateContractFromBidModal";
import { RevokeAwardModal } from "./RevokeAwardModal";

const bidDocuments = (bid: ReceivedPublicBidDto) =>
  [
    ...(bid.documents ?? []),
    ...((bid.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
  ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index);

export const AwardOutcome = ({ event, readOnly = false }: { event: SourcingEventV2Dto; readOnly?: boolean }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { data, isLoading: areBidsLoading } = useListPublicBidsForEventQuery({ eventId: event.id });
  const { data: evaluation, isError: isEvaluationError } = useGetEvaluationQuery({ eventId: event.id });
  const [confirm, { isLoading: isConfirming }] = useConfirmAwardMutation();
  const [contractModalBidId, setContractModalBidId] = useState<string | null>(null);
  const [isRevokeModalOpen, setRevokeModalOpen] = useState(false);

  const award = event.award;
  const bids = data?.bids ?? [];
  const winningBidIds = award?.winningBidIds ?? [];
  const winners = winningBidIds
    .map((id) => bids.find((b) => b.id === id))
    .filter((b): b is ReceivedPublicBidDto => !!b);
  const contractModalBid = winners.find((w) => w.id === contractModalBidId);
  const isDecision = event.status === SourcingEventStatus.AwardDecision;

  const standstillExpiresAt = award?.standstillExpiresAt ?? undefined;
  const standstillDispatched = standstillExpiresAt !== undefined;
  // Memoize the epoch ms from the stable string so the effect below only re-runs when the deadline
  // actually changes, not on every render (a fresh `new Date()` would change identity each time).
  const standstillExpiryMs = useMemo(
    () => (standstillExpiresAt ? new Date(standstillExpiresAt).getTime() : undefined),
    [standstillExpiresAt],
  );

  // Re-render exactly when the standstill deadline passes so the Confirm button enables itself without
  // needing a manual refresh; Date.now() alone only re-evaluates on an unrelated render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (standstillExpiryMs === undefined) return;
    const msUntilExpiry = standstillExpiryMs - Date.now();
    if (msUntilExpiry <= 0) {
      // The deadline may already be in the past (e.g. a refetch after the dev fast-forward skip) —
      // bump `now` once so the Confirm button enables without a reload, but never loop.
      setNow((current) => (current >= standstillExpiryMs ? current : Date.now()));
      return;
    }
    const timer = setTimeout(() => setNow(Date.now()), msUntilExpiry);
    return () => clearTimeout(timer);
  }, [standstillExpiryMs]);

  const standstillElapsed = standstillExpiryMs !== undefined && now >= standstillExpiryMs;
  const standstillDaysRemaining =
    standstillExpiryMs !== undefined
      ? Math.max(0, Math.ceil((standstillExpiryMs - now) / (1000 * 60 * 60 * 24)))
      : 0;

  const onConfirm = async () => {
    try {
      await confirm({ eventId: event.id }).unwrap();
      toast({ title: t("Award confirmed"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="6">
        <Text fontWeight="semibold" fontSize="sm" color="smMuted" textTransform="uppercase" mb="3">
          {t("Awarded suppliers")}
        </Text>
        <Stack spacing="2">
          {areBidsLoading ? (
            <Center py="2">
              <Spinner size="sm" color="smPrimary" />
            </Center>
          ) : winners.length > 0 ? (
            winners.map((winner) => (
              <HStack key={winner.id} spacing="3">
                <FiAward color="var(--chakra-colors-smPrimary)" />
                <Text fontWeight="semibold" fontSize="lg" color="smText">
                  {winner.ownerName || t("Unknown")}
                </Text>
                <Badge colorScheme="teal">{t("Winner")}</Badge>
              </HStack>
            ))
          ) : (
            <Text color="smMuted">{t("Unknown")}</Text>
          )}
        </Stack>
        {award?.internalNote && (
          <Box mt="4">
            <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase">
              {t("Internal note")}
            </Text>
            <Text color="smText" whiteSpace="pre-wrap">
              {award.internalNote}
            </Text>
          </Box>
        )}
      </Box>

      {isEvaluationError ? (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smError">
            {t("Could not load the evaluation.")}
          </Text>
        </Box>
      ) : (
        evaluation &&
        evaluation.bids.length > 0 && (
          <AwardBidsTable evaluation={evaluation} mode="confirmed" selectedBidIds={winningBidIds} />
        )
      )}

      <AwardLetterWorkbench event={event} />

      {isDecision ? (
        <Box bg="smBackgroundSecondary" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
          {!standstillDispatched ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Send the award letters to start the standstill period before confirming the award.")}
            </Text>
          ) : standstillElapsed ? (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("The standstill period has passed. You can now confirm the award.")}
            </Text>
          ) : (
            <Text fontSize="sm" color="smMuted" mb="3">
              {t("Standstill period ends {{date}} ({{count}} days remaining). The award can be confirmed after this.", {
                date: standstillExpiresAt ? displayDate(standstillExpiresAt) : "",
                count: standstillDaysRemaining,
              })}
            </Text>
          )}
          <HStack spacing="3">
            <Button colorScheme="teal" onClick={onConfirm} isLoading={isConfirming} isDisabled={!standstillElapsed}>
              {t("Confirm award")}
            </Button>
            {!readOnly && (
              <Button variant="outline" colorScheme="red" onClick={() => setRevokeModalOpen(true)}>
                {t("Change award decision")}
              </Button>
            )}
          </HStack>
        </Box>
      ) : (
        <Stack spacing="3">
          {winners.map((winner) => {
            const { contractId } = winner;
            return (
              <Box key={winner.id} bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="5">
                <HStack justify="space-between">
                  <HStack spacing="3">
                    <FiFileText />
                    <Text color="smText">{winner.ownerName || t("Unknown")}</Text>
                  </HStack>
                  {contractId ? (
                    <Button
                      variant="outline"
                      colorScheme="teal"
                      rightIcon={<FiExternalLink />}
                      onClick={() => navigate(contractUrl(contractId, winner.contractState, event.id))}
                    >
                      {t("Go to contract")}
                    </Button>
                  ) : readOnly ? (
                    <Text fontSize="sm" color="smMuted">
                      {t("No contract")}
                    </Text>
                  ) : (
                    <Button colorScheme="teal" onClick={() => setContractModalBidId(winner.id)}>
                      {t("Create contract")}
                    </Button>
                  )}
                </HStack>
              </Box>
            );
          })}
        </Stack>
      )}

      <AwardDecisionHistory event={event} />

      {isDecision && !readOnly && (
        <RevokeAwardModal
          eventId={event.id}
          lettersDispatched={standstillDispatched}
          isOpen={isRevokeModalOpen}
          onClose={() => setRevokeModalOpen(false)}
        />
      )}

      {contractModalBid && (
        <CreateContractFromBidModal
          eventId={event.id}
          bidId={contractModalBid.id}
          supplierName={contractModalBid.ownerName}
          defaultTitle={event.eventData.title}
          documents={bidDocuments(contractModalBid)}
          isOpen={contractModalBidId !== null}
          onClose={() => setContractModalBidId(null)}
        />
      )}
    </Stack>
  );
};
